// src/services/useTemplateStore.js
/**
 * Zustand store para gerenciar templates de WhatsApp Cloud API.
 *
 * Responsável por:
 * - Buscar e armazenar templates do servidor
 * - Sincronizar templates com a Meta Graph API
 * - Filtrar templates por status, categoria, idioma
 * - Gerenciar seleção de template para envio
 */

import { create } from "zustand";
import { api } from "./api";

const useTemplateStore = create((set, get) => ({
  // Estado
  templates: [],
  selectedTemplate: null,
  isLoading: false,
  isSyncing: false,
  error: null,
  lastSyncedAt: null,
  meta: {
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
  },

  // Filtros
  filters: {
    status: "",
    category: "",
    language: "",
    search: "",
  },

  // Ações de filtros
  setFilter: (key, value) =>
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    })),

  clearFilters: () =>
    set({
      filters: {
        status: "",
        category: "",
        language: "",
        search: "",
      },
    }),

  // Ações de seleção
  selectTemplate: (template) => set({ selectedTemplate: template }),
  clearSelection: () => set({ selectedTemplate: null }),

  /**
   * Busca templates para uma integração.
   */
  fetchTemplates: async (integrationId, options = {}) => {
    if (!integrationId) {
      set({ templates: [], error: "ID da integração não fornecido" });
      return [];
    }

    try {
      set({ isLoading: true, error: null });

      const { filters } = get();
      const params = {
        ...options,
        status: filters.status || undefined,
        category: filters.category || undefined,
        language: filters.language || undefined,
        search: filters.search || undefined,
      };

      // Remove parâmetros vazios
      Object.keys(params).forEach(
        (key) => params[key] === undefined && delete params[key]
      );

      const response = await api.get(
        `/api/integrations/${integrationId}/templates`,
        { params }
      );

      if (response.data?.success) {
        const templates = response.data.templates || [];
        const meta = response.data.meta || {};

        set({
          templates,
          meta: {
            total: meta.total || templates.length,
            approved: meta.approved || 0,
            pending: meta.pending || 0,
            rejected: meta.rejected || 0,
          },
          lastSyncedAt: meta.last_synced_at
            ? new Date(meta.last_synced_at)
            : null,
          isLoading: false,
        });

        return templates;
      }

      set({
        templates: [],
        isLoading: false,
        error: response.data?.error || "Erro ao buscar templates",
      });
      return [];
    } catch (err) {
      console.error("Erro ao buscar templates:", err);
      set({
        templates: [],
        isLoading: false,
        error: err.response?.data?.error || err.message,
      });
      return [];
    }
  },

  /**
   * Busca apenas templates aprovados (prontos para uso).
   */
  fetchApprovedTemplates: async (integrationId) => {
    if (!integrationId) {
      set({ templates: [], error: "ID da integração não fornecido" });
      return [];
    }

    try {
      set({ isLoading: true, error: null });

      const response = await api.get(
        `/api/integrations/${integrationId}/templates/approved`
      );

      if (response.data?.success) {
        const templates = response.data.templates || [];

        set({
          templates,
          meta: {
            ...get().meta,
            total: templates.length,
            approved: templates.length,
          },
          isLoading: false,
        });

        return templates;
      }

      set({
        templates: [],
        isLoading: false,
        error: response.data?.error || "Erro ao buscar templates aprovados",
      });
      return [];
    } catch (err) {
      console.error("Erro ao buscar templates aprovados:", err);
      set({
        templates: [],
        isLoading: false,
        error: err.response?.data?.error || err.message,
      });
      return [];
    }
  },

  /**
   * Sincroniza templates com a Meta Graph API.
   */
  syncTemplates: async (integrationId) => {
    if (!integrationId) {
      return { success: false, error: "ID da integração não fornecido" };
    }

    try {
      set({ isSyncing: true, error: null });

      const response = await api.post(
        `/api/integrations/${integrationId}/templates/sync`
      );

      if (response.data?.success) {
        // Atualiza a lista após sincronização
        await get().fetchTemplates(integrationId);

        set({
          isSyncing: false,
          lastSyncedAt: new Date(),
        });

        return {
          success: true,
          results: response.data.results,
          message: response.data.message,
        };
      }

      set({
        isSyncing: false,
        error: response.data?.error || "Erro ao sincronizar templates",
      });

      return {
        success: false,
        error: response.data?.error || "Erro ao sincronizar",
      };
    } catch (err) {
      console.error("Erro ao sincronizar templates:", err);
      set({
        isSyncing: false,
        error: err.response?.data?.error || err.message,
      });
      return {
        success: false,
        error: err.response?.data?.error || err.message,
      };
    }
  },

  /**
   * Busca um template específico por nome.
   */
  getTemplateByName: async (integrationId, templateName, language = "pt_BR") => {
    if (!integrationId || !templateName) {
      return null;
    }

    try {
      const response = await api.get(
        `/api/integrations/${integrationId}/templates/by-name/${templateName}`,
        { params: { language } }
      );

      if (response.data?.success) {
        return response.data.template;
      }

      return null;
    } catch (err) {
      console.error("Erro ao buscar template por nome:", err);
      return null;
    }
  },

  /**
   * Retorna templates filtrados localmente.
   */
  getFilteredTemplates: () => {
    const { templates, filters } = get();

    return templates.filter((template) => {
      if (filters.status && template.status !== filters.status) return false;
      if (filters.category && template.category !== filters.category) return false;
      if (filters.language && template.language !== filters.language) return false;
      if (
        filters.search &&
        !template.name.toLowerCase().includes(filters.search.toLowerCase())
      )
        return false;
      return true;
    });
  },

  /**
   * Formata o tempo desde a última sincronização.
   */
  getLastSyncFormatted: () => {
    const { lastSyncedAt } = get();
    if (!lastSyncedAt) return null;

    const now = new Date();
    const diff = now - lastSyncedAt;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (days > 0) return `há ${days} dia${days > 1 ? "s" : ""}`;
    if (hours > 0) return `há ${hours} hora${hours > 1 ? "s" : ""}`;
    if (minutes > 0) return `há ${minutes} minuto${minutes > 1 ? "s" : ""}`;
    return "agora mesmo";
  },

  /**
   * Limpa o store.
   */
  reset: () =>
    set({
      templates: [],
      selectedTemplate: null,
      isLoading: false,
      isSyncing: false,
      error: null,
      lastSyncedAt: null,
      meta: { total: 0, approved: 0, pending: 0, rejected: 0 },
      filters: { status: "", category: "", language: "", search: "" },
    }),
}));

export default useTemplateStore;
